import {Component, Inject, LOCALE_ID} from '@angular/core';

import {featuredReviews, PLAY_RATING, PLAY_STORE_URL, PlayReview} from './product-reviews.data';

/**
 * Real Google Play reviews, quoted verbatim in their original language. The store's own score
 * leads, large, because it is the strongest proof on the page; beside it, three reviews picked
 * per site language, each showing one thing a visitor is deciding on. They stand still: a review
 * that drifts past cannot be read, and three that matter say more than a wall of them.
 */
@Component({
    selector: 'app-product-reviews',
    imports: [],
    template: `
      <section class="reviews" aria-labelledby="reviews-title">
        <div class="score">
          <h2 class="eyebrow" id="reviews-title" i18n="@@reviews-eyebrow">From Google Play</h2>
          <p class="score-figure">{{ rating.score }}</p>
          <div class="stars stars-large" role="img" [attr.aria-label]="rating.score + ' / 5'">
            <span class="stars-back" aria-hidden="true">★★★★★</span>
            <span class="stars-front" aria-hidden="true" [style.width.%]="rating.score / 5 * 100">★★★★★</span>
          </div>
          <p class="score-meta" i18n="@@reviews-ratings-on-google-play">{{ rating.count }} ratings on Google Play</p>
          <a class="reviews-all" [href]="storeUrl" target="_blank" rel="noopener noreferrer">
            <span i18n="@@reviews-read-all">Read all reviews on Google Play</span>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </a>
        </div>

        <ul class="quotes">
          @for (review of reviews; track review.author; let i = $index) {
            <li>
              <figure class="quote">
                <blockquote [attr.lang]="review.lang">{{ review.text }}</blockquote>
                <figcaption>
                  <span class="avatar" aria-hidden="true" [class]="'avatar tone-' + (i % 3)">{{ review.author.charAt(0) }}</span>
                  <span class="author">{{ review.author }}</span>
                  <span class="date">{{ formatMonth(review.month) }}</span>
                  <span class="stars" role="img" [attr.aria-label]="review.score + ' / 5'">
                    <span class="stars-back" aria-hidden="true">★★★★★</span>
                    <span class="stars-front" aria-hidden="true" [style.width.%]="review.score / 5 * 100">★★★★★</span>
                  </span>
                  @if (review.lang !== siteLang) {
                    <span class="lang-tag">{{ review.lang.toUpperCase() }}</span>
                  }
                </figcaption>
              </figure>
            </li>
          }
        </ul>
      </section>
    `,
    styles: [`
        .reviews {
            /* The home page's container: content on the same left edge as every other section. */
            max-width: 1168px;
            margin: 0 auto;
            padding: 88px 24px;
            display: grid;
            grid-template-columns: minmax(0, 300px) minmax(0, 1fr);
            gap: 64px;
            align-items: start;
        }

        .eyebrow {
            margin: 0 0 10px;
            font-size: 0.95rem;
            font-weight: 700;
            color: var(--cb-gold-deep, #8A5800);
        }

        .score-figure {
            margin: 0;
            font-family: var(--cb-display);
            font-size: clamp(4rem, 2.8rem + 4vw, 5.5rem);
            font-weight: 800;
            line-height: 0.95;
            letter-spacing: -0.05em;
            color: var(--cb-ink, #131A22);
        }

        /* Stars: the filled row laid over the empty one, cut to the score. A deep amber, so they
           clear the 3:1 a meaningful graphic needs on cream. */
        .stars {
            position: relative;
            display: inline-block;
            line-height: 1;
            letter-spacing: 2px;
            white-space: nowrap;
        }

        .stars-back {
            color: #E4DCCD;
        }

        .stars-front {
            position: absolute;
            inset: 0 auto 0 0;
            overflow: hidden;
            color: #C27C00;
        }

        .stars-large {
            margin: 14px 0 8px;
            font-size: 1.7rem;
        }

        .score-meta {
            margin: 0 0 24px;
            font-size: 0.98rem;
            color: var(--cb-muted, #5E6772);
        }

        .reviews-all {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-weight: 700;
            color: var(--cb-teal, #08735F);
            text-decoration: none;
        }

        .reviews-all svg {
            transition: transform 0.2s ease;
        }

        .reviews-all:hover span {
            text-decoration: underline;
            text-underline-offset: 4px;
        }

        .reviews-all:hover svg {
            transform: translateX(3px);
        }

        .reviews-all:focus-visible {
            outline: 2px solid var(--cb-teal, #08735F);
            outline-offset: 4px;
            border-radius: 4px;
        }

        .quotes {
            list-style: none;
            margin: 0;
            padding: 0;
            border-top: 1px solid var(--cb-line, #E7DFD2);
        }

        .quotes li {
            border-bottom: 1px solid var(--cb-line, #E7DFD2);
        }

        .quote {
            margin: 0;
            padding: 22px 0;
        }

        /* Body size: the quotes are proof beside the score, not the section's headline. */
        blockquote {
            margin: 0 0 12px;
            font-size: 1.05rem;
            font-weight: 500;
            line-height: 1.6;
            color: var(--cb-ink, #131A22);
            max-width: 46em;
        }

        blockquote::before { content: '“'; }
        blockquote::after { content: '”'; }

        figcaption {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 8px 12px;
            font-size: 0.92rem;
        }

        .avatar {
            display: inline-grid;
            place-items: center;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            font-weight: 700;
            color: white;
        }

        .tone-0 { background: var(--cb-teal, #08735F); }
        .tone-1 { background: var(--cb-gold-deep, #8A5800); }
        .tone-2 { background: #B51F58; }

        .author {
            font-weight: 700;
            color: var(--cb-ink, #131A22);
        }

        .date {
            color: var(--cb-muted, #5E6772);
        }

        .lang-tag {
            padding: 2px 7px;
            border: 1px solid var(--cb-line, #E7DFD2);
            border-radius: 6px;
            font-size: 0.75rem;
            font-weight: 700;
            color: var(--cb-muted, #5E6772);
        }

        @media (max-width: 900px) {
            .reviews {
                grid-template-columns: minmax(0, 1fr);
                gap: 36px;
                padding: 72px 16px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .reviews-all svg { transition: none; }
        }
    `],
})
export class ProductReviewsComponent {
    readonly rating = PLAY_RATING;
    readonly storeUrl = PLAY_STORE_URL;
    readonly reviews: PlayReview[];
    readonly siteLang: string;

    constructor(@Inject(LOCALE_ID) private locale: string) {
        this.reviews = featuredReviews(locale);
        this.siteLang = locale.split('-')[0];
    }

    /** "2026-01" → "Jan 2026" in the site locale, without pulling in a date library. */
    formatMonth(month: string): string {
        const [y, m] = month.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString(this.locale, {month: 'short', year: 'numeric'});
    }
}
